import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { InputCP } from 'submodules/nerit-framework-ui/common/components/form-fields/input/InputCP'
import { BasicModalCPProps, ModalCP } from 'submodules/nerit-framework-ui/common/components/modal/ModalCP'
import { ButtonCP } from 'submodules/nerit-framework-ui/common/components/button/ButtonCP'
import { TagCP } from 'submodules/nerit-framework-ui/common/components/tag/TagCP'
import { RowCP } from 'submodules/nerit-framework-ui/common/components/grid/row/RowCP'
import { ColumnCP } from 'submodules/nerit-framework-ui/common/components/grid/column/ColumnCP'
import { useRequest } from 'submodules/nerit-framework-ui/common/request-manager/use-request/UseRequest'
import { useFormStateManager } from 'submodules/nerit-framework-ui/common/form-state-manager/UseFormStateManager'
import { FormStateManagerUtils } from 'submodules/nerit-framework-ui/common/form-state-manager/FormStateManagerUtils'
import { RequestUtils } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/RequestUtils'
import { NotificationHelper } from 'submodules/nerit-framework-ui/common/components/notification/inner/NotificationHelper'
import { NameAndCodeResponseDTO } from 'submodules/nerit-framework-utils/sdk-utils/dtos/response/NameAndCodeResponseDTO'
import { ParameterGroupRequests } from 'submodules/nerit-aquatec-sdk/services/parameter-group/ParameterGroupRequests'
import { ParameterGroupResponseDTO } from 'submodules/nerit-aquatec-sdk/services/parameter-group/dtos/responses/ParameterGroupResponseDTO'
import { ParameterGroupFormModel } from 'modules/collection-performance/components/parameter-group/modal-parameter-group/inner/ParameterGroupFormModel'
import { SelectFieldCP } from 'modules/fields/components/select-field/SelectFieldCP'

interface ICPProps extends BasicModalCPProps {
    parameterGroup?: ParameterGroupResponseDTO
}

/**
 * Modal de criacao/edicao de Grupo de Parametros.
 *
 * - Na criacao os parametros selecionados vao junto com o create.
 * - Na edicao o nome e salvo no OK e a gestao de membros e feita ao vivo.
 * Nao ha restricao de exclusividade: o mesmo parametro pode entrar em varios grupos.
 */
export function ModalParameterGroupCP(props: ICPProps): JSX.Element {

    const isEditing = !!props.parameterGroup

    const [formModel, setFormModel] = useState<ParameterGroupFormModel>(new ParameterGroupFormModel())
    const formStateManager = useFormStateManager(formModel)

    const detailRequest = useRequest<ParameterGroupResponseDTO>()

    const saveNameRequest = useRequest<void>('none')
    useEffect(onSaveNameRequestChange, [saveNameRequest.isAwaiting])

    const addRequest = useRequest<void>('none')
    useEffect(onAddRequestChange, [addRequest.isAwaiting])

    const removeRequest = useRequest<void>('none')
    useEffect(onRemoveRequestChange, [removeRequest.isAwaiting])

    useEffect(init, [props.visible])

    /**
     */
    function init(): void {

        if (!props.visible) {
            setFormModel(new ParameterGroupFormModel())
            return
        }

        setFormModel(new ParameterGroupFormModel({ name: props.parameterGroup?.name }))

        if (isEditing)
            detailRequest.runRequest(ParameterGroupRequests.get(props.parameterGroup!.code))
    }

    /**
     */
    async function onClickSave(): Promise<void> {

        if (!await FormStateManagerUtils.validateRequiredFields(formStateManager))
            return

        const formValues = formStateManager.getFormValues()!

        if (isEditing)
            saveNameRequest.runRequest(ParameterGroupRequests.update(props.parameterGroup!.code, { name: formValues.name }))
        else
            saveNameRequest.runRequest(ParameterGroupRequests.create({ name: formValues.name, fieldCodes: formValues.fieldCodesToAdd }))
    }

    /**
     */
    function onSaveNameRequestChange(): void {

        if (!RequestUtils.isValidRequestReturn(saveNameRequest, NotificationHelper.DEFAULT_ERROR_SAVE_MESSAGE, NotificationHelper.DEFAULT_SUCCESS_SAVE_MESSAGE, true))
            return

        props.onSave()
    }

    /**
     */
    function onClickAddFields(): void {

        const fieldCodes = formStateManager.getFieldValue('fieldCodesToAdd') as number[] | undefined
        if (!fieldCodes?.length)
            return

        addRequest.runRequest(ParameterGroupRequests.addFields(props.parameterGroup!.code, { fieldCodes }))
    }

    /**
     */
    function onAddRequestChange(): void {

        if (!RequestUtils.isValidRequestReturn(addRequest, NotificationHelper.DEFAULT_ERROR_SAVE_MESSAGE, 'Parâmetros adicionados!', true))
            return

        formStateManager.changeFieldValue('fieldCodesToAdd', [])
        detailRequest.runRequest(ParameterGroupRequests.get(props.parameterGroup!.code))
    }

    /**
     */
    function onClickRemoveField(fieldCode: number): void {
        removeRequest.runRequest(ParameterGroupRequests.removeField(props.parameterGroup!.code, fieldCode))
    }

    /**
     */
    function onRemoveRequestChange(): void {

        if (!RequestUtils.isValidRequestReturn(removeRequest, NotificationHelper.DEFAULT_ERROR_DELETE_MESSAGE, NotificationHelper.DEFAULT_SUCCESS_DELETE_MESSAGE, true))
            return

        detailRequest.runRequest(ParameterGroupRequests.get(props.parameterGroup!.code))
    }

    const members: NameAndCodeResponseDTO[] = detailRequest.responseData?.fields ?? []

    return (
        <ModalCP
            visible={props.visible}
            actionLoading={saveNameRequest.isAwaiting}
            onOk={onClickSave}
            onCancel={props.onCancel}
            title={isEditing ? 'Editar Grupo de Parâmetros' : 'Novo Grupo de Parâmetros'}
            width={520}
        >
            <InputCP
                label={'Nome'}
                required={true}
                formStateManager={formStateManager}
                fieldName={'name'}
            />

            {
                !isEditing &&
                <SelectFieldCP
                    isMany={true}
                    formStateManager={formStateManager}
                    fieldName={'fieldCodesToAdd'}
                />
            }

            {
                isEditing &&
                <>
                    <RowCP>
                        <ColumnCP size={19}>
                            <SelectFieldCP
                                isMany={true}
                                formStateManager={formStateManager}
                                fieldName={'fieldCodesToAdd'}
                            />
                        </ColumnCP>
                        <ColumnCP size={5}>
                            <ButtonCP
                                icon={'plus'}
                                type={'primary'}
                                marginTop={23}
                                loading={addRequest.isAwaiting}
                                onClick={onClickAddFields}
                            >
                                {'Adicionar'}
                            </ButtonCP>
                        </ColumnCP>
                    </RowCP>

                    <MembersWrapperSCP>
                        {
                            members.map((member) => (
                                <TagCP
                                    key={member.code}
                                    content={member.name}
                                    color={'blue'}
                                    onClose={() => onClickRemoveField(member.code)}
                                />
                            ))
                        }
                        {
                            !detailRequest.isAwaiting && !members.length &&
                            'Nenhum parâmetro vinculado a este grupo.'
                        }
                    </MembersWrapperSCP>
                </>
            }
        </ModalCP>
    )
}

const MembersWrapperSCP = styled.div`
    margin-top: 15px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`
